import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../lib/backendUrl';

const BACKEND_URL = getBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const TOKEN_KEY = 'myarc_session_token';

export const api = axios.create({ baseURL: API, withCredentials: true });

// Inject stored Bearer token on every request so auth works across
// domains even when cross-site cookies are blocked by the browser.
api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrap setUser so callers can also pass a session_token to persist
  const setUserWithToken = useCallback((userData, sessionToken) => {
    if (sessionToken) localStorage.setItem(TOKEN_KEY, sessionToken);
    setUser(userData);
  }, []);

  const checkAuth = useCallback(async () => {
    // If we have a stored token but no user yet, try to restore the session
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) { setLoading(false); return; }
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
    } catch {
      // Token expired or backend down — clear stale token
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const value = { user, setUser: setUserWithToken, loading, checkAuth, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
